// apps/web/src/routes.tsx
import React from 'react';

const Home = React.lazy(() => import('./pages/Home/Home'));
const Dashboard = React.lazy(() => import('./pages//Dashboard/Dashboard'));
const Settings = React.lazy(() => import('./pages/Settings/Settings'));
const SignupPage = React.lazy(() => import('./pages/SignupPage/SignupPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage/LoginPage'));
const Simulate = React.lazy(() => import('./pages/Simulate/Simulate'));
const SimulateHome = React.lazy(
	() => import('./pages/Simulate/SimulateHome/SimulateHome'),
);
const ImpactLab = React.lazy(() => import('./pages/ImpactLab/ImpactLab'));
const MarketReplay = React.lazy(
	() => import('./pages/ImpactLab/MarketReplay/MarketReplay'),
);
const Quizzes = React.lazy(() => import('./pages/Quizzes/Quizzes'));
const DiscoveryQuiz = React.lazy(
	() => import('./pages/Quizzes/DiscoveryQuiz/DiscoveryQuiz'),
);
const RewardVault = React.lazy(() => import('./pages/RewardVault/RewardVault'));
const Progress = React.lazy(() => import('./pages/Progress/Progress'));

// ── Learn IA ──────────────────────────────────────────────────────────────
const Learn = React.lazy(() => import('./pages/Learn/Learn')); // Hub
const LearnMap = React.lazy(() => import('./pages/Learn/LearnMap/LearnMap')); // Map
const Schools = React.lazy(() => import('./pages/Schools/Schools'));
const School = React.lazy(() => import('./pages/Schools/School/School'));
const Playlists = React.lazy(() => import('./pages/Playlists/Playlists'));
const Playlist = React.lazy(() => import('./pages/Playlists/Playlist/Playlist'));
const Decks = React.lazy(() => import('./pages/Decks/Decks'));
const DeckDetail = React.lazy(() => import('./pages/Decks/DeckDetail/DeckDetail'));
const CreateDeck = React.lazy(() => import('./pages/Decks/CreateDeck/CreateDeck'));
const DeckPractice = React.lazy(
	() => import('./pages/Decks/DeckPractice/DeckPractice'),
);
const LessonLibrary = React.lazy(
	() => import('./pages/LessonLibrary/LessonLibrary'),
);
const Module = React.lazy(() => import('./pages/Module/Module'));
const Lesson = React.lazy(() => import('./pages/Lesson/Lesson'));
const Quiz = React.lazy(() => import('./pages/Quiz/Quiz'));
const Flashcards = React.lazy(() => import('./pages/Flashcards/Flashcards'));
const ReviewLanding = React.lazy(
	() => import('./pages/Flashcards/ReviewLanding/ReviewLanding'),
);

// Legal pages — public, no auth required.
const TermsOfService = React.lazy(() => import('./pages/Legal/TermsOfService/TermsOfService'));
const Disclaimer = React.lazy(() => import('./pages/Legal/Disclaimer/Disclaimer'));
const PrivacyPolicy = React.lazy(() => import('./pages/Legal/PrivacyPolicy/PrivacyPolicy'));

export const appRoutes = [
	{ path: '/', element: <Home />, title: 'Home', exact: true },
	{ path: '/dashboard', element: <Dashboard />, title: 'Dashboard' },
	// Simulate — the home page introduces the two rooms; Plan and Lab live
	// under ONE param route so switching views never remounts the page (the
	// shared chart's mount-once band-fade bridge depends on this).
	{ path: '/simulate', element: <SimulateHome />, title: 'Simulate' },
	{ path: '/simulate/:view', element: <Simulate /> },
	{ path: '/settings', element: <Settings />, title: 'Settings' },
	{ path: '/signup', element: <SignupPage />, title: 'Sign Up' },
	{ path: '/login', element: <LoginPage />, title: 'Login' },
	{ path: '/impact-lab', element: <ImpactLab />, title: 'Impact Lab' },
	// Market Replay — static segment, registered BEFORE :scenarioId so the
	// reserved 'market-replay' slug can never be captured as a scenario id.
	{ path: '/impact-lab/market-replay', element: <MarketReplay /> },
	{
		path: '/impact-lab/market-replay/:replayId',
		element: <MarketReplay />,
	},
	{ path: '/impact-lab/:scenarioId', element: <ImpactLab /> },
	// Quizzes — the discovery quiz hub + runners. Anon-accessible.
	{ path: '/quizzes', element: <Quizzes />, title: 'Quizzes' },
	{ path: '/quizzes/:quizId', element: <DiscoveryQuiz />, title: 'Quiz' },
	{ path: '/rewards', element: <RewardVault />, title: 'Gem Vault' },
	{ path: '/progress', element: <Progress />, title: 'Badges' },

	// ── Learn ────────────────────────────────────────────────────────────
	// Static wings first, param routes after. The lesson detail moved from
	// the old /learn/:categoryId/:moduleId (where categoryId duplicated the
	// moduleId) to a clean /learn/map/:moduleId tree. The orphaned
	// /learn/:categoryId Category page is removed.
	{ path: '/learn', element: <Learn />, title: 'Learn' },
	{ path: '/learn/map', element: <LearnMap />, title: 'Map' },
	// Review — landing page (counts, explainer, Start). The flip-card session
	// lives at /review/session so the nav tab never drops the user straight
	// into cards. Both session entry points (daily review here, per-module
	// below) render the same Flashcards component; it detects the missing
	// :moduleId and sources due cards across every module (see isDailyReview).
	{ path: '/review', element: <ReviewLanding />, title: 'Review' },
	{ path: '/review/session', element: <Flashcards />, title: 'Review' },
	{ path: '/learn/schools', element: <Schools />, title: 'Schools' },
	{
		path: '/learn/schools/:schoolId',
		element: <School />,
		title: 'School',
	},
	{ path: '/learn/playlists', element: <Playlists />, title: 'Playlists' },
	{
		path: '/learn/playlists/:playlistId',
		element: <Playlist />,
		title: 'Playlist',
	},
	// Lesson Library — browse/filter every lesson. Static wing, listed before
	// the '/learn/map/:moduleId' param routes (distinct prefix, so no shadowing).
	{ path: '/learn/library', element: <LessonLibrary />, title: 'Library' },
	// Decks — browse home for all unlocked mastery cards (distinct prefix,
	// listed with the other static wings before the :moduleId param routes).
	{ path: '/learn/decks', element: <Decks />, title: 'Decks' },
	// Deck create/edit/practice + detail. Static '/new' is listed BEFORE the
	// ':deckId' param route so it can never be captured as a deck id, and all
	// four sit before '/learn/map/:moduleId' (distinct prefix, no shadowing).
	{ path: '/learn/decks/new', element: <CreateDeck />, title: 'New deck' },
	{
		path: '/learn/decks/:deckId/edit',
		element: <CreateDeck />,
		title: 'Edit deck',
	},
	{
		path: '/learn/decks/:deckId/practice',
		element: <DeckPractice />,
		title: 'Practice',
	},
	{ path: '/learn/decks/:deckId', element: <DeckDetail />, title: 'Deck' },
	{ path: '/learn/map/:moduleId', element: <Module />, title: 'Module' },
	// quiz/flashcards listed before :lessonId so they win in order-based routers.
	{
		path: '/learn/map/:moduleId/quiz/:assessmentId',
		element: <Quiz />,
	},
	{
		path: '/learn/map/:moduleId/flashcards',
		element: <Flashcards />,
	},
	{
		path: '/learn/map/:moduleId/:lessonId',
		element: <Lesson />,
		title: 'Lesson',
	},

	// ── Legal ──────────────────────────────────────────────────────────────
	{
		path: '/legal/terms',
		element: <TermsOfService />,
		title: 'Terms of Service',
	},
	{ path: '/legal/disclaimer', element: <Disclaimer />, title: 'Disclaimer' },
	{
		path: '/legal/privacy',
		element: <PrivacyPolicy />,
		title: 'Privacy Policy',
	},
	{ path: '*', element: <h1>404 - Page Not Found</h1>, title: '404' },
];
